import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Button } from '../Ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../Ui/select';
import { Input } from '../Ui/input';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { Calendar } from 'lucide-react';
import axios from 'axios';
import { settings } from '../utils/Settings';
import PropTypes from 'prop-types';

const AttendanceFilters = ({
  showLea,
  onToggleLeavers,
  dateRange,
  onDateRangeChange,
  onFiltersChange, // ✅ New prop for filters
  onMonthNavigation // ✅ New prop for month navigation
}) => {
  const [SchoolsId, setSchoolsId] = useState();
  const [schools, setSchools] = useState([]);
  const [laSelection, setLaSelection] = useState({ in: true, out: true });

  

  // UASC state
  const [uascSelection, setUascSelection] = useState({
    yes: false,
    no: false
  });

  // Care Status state
  const [careChecks, setCareChecks] = useState({
    CIC: false,
    CIN: false,
    CP: false,
    PCIN: false,
    PCP: false,
    EHCP: false
  });

  const [genderSelection, setGenderSelection] = useState([]);
  const genderOptions = [
    "Male",
    "Female",
    "Not Specified",
    "Indeterminate"
  ];

  const [yearGroupSelection, setYearGroupSelection] = useState([]);
  const yearGroups = [
    "R 2025", "N2 2025", "N3 2025", "N4 2025",
    "Y1 2025", "Y2 2025", "Y3 2025", "Y4 2025",
    "Y5 2025", "Y6 2025", "Y7 2025", "Y8 2025",
    "Y9 2025", "Y10 2025", "Y11 2025"
  ];

  const [attendanceSelection, setAttendanceSelection] = useState([]);
  const attendanceOptions = [
    "/", "\\", "B", "D", "K", "J", "L", "P", "V", "W",
    "C", "C1", "C2", "E", "H", "I", "J1", "M", "R", "S",
    "T", "G", "N", "O", "U", "Q", "X", "Y", "Y1", "Y2",
    "Y3", "Y4", "Y5", "Y6", "Y7", "Z", "#", "-"
  ];

  const [senChecks, setSenChecks] = useState({
    Unknown: true,
    None: true,
    'SEN Support': true,
    EHCP: true,
    EHCNA: true,
  });

  const [ealSelection, setEalSelection] = useState({ yes: true, no: true });
  const [datePreset, setDatePreset] = useState('custom');
  const [searchTerm, setSearchTerm] = useState(''); // ✅ Added search term

  const userId = localStorage.getItem("userId");
  const showLeavers = !!showLea;

  // ✅ Debounced filter building function to prevent excessive API calls
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  
  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay
    
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // ✅ Memoized filter building function for better performance
  const buildFilters = useCallback(() => {
    const filters = {};

    // Gender - comma separated
    if (genderSelection.length > 0) {
      filters.gender = genderSelection.join(",");
    }

    // Year Group - comma separated
    if (yearGroupSelection.length > 0) {
      filters.yearGroup = yearGroupSelection.join(",");
    }

    // Attendance Code - comma separated
    if (attendanceSelection.length > 0) {
      filters.attendanceCode = attendanceSelection.join(",");
    }

    // Local Authority
    const laValues = [];
    if (laSelection.in) laValues.push("In");
    if (laSelection.out) laValues.push("Out");
    if (laValues.length > 0 && laValues.length < 2) {
      filters.localAuthority = laValues.join(",");
    }

    // SEN
    const senValues = Object.entries(senChecks)
      .filter(([, checked]) => checked)
      .map(([key]) => key);
    if (senValues.length > 0 && senValues.length < 5) {
      filters.send = senValues.join(",");
    }

    // EAL
    const ealValues = [];
    if (ealSelection.yes) ealValues.push("YES");
    if (ealSelection.no) ealValues.push("NO");
    if (ealValues.length > 0 && ealValues.length < 2) {
      filters.eal = ealValues.join(",");
    }

    // UASC
    const uascValues = [];
    if (uascSelection.yes) uascValues.push("YES");
    if (uascSelection.no) uascValues.push("NO");
    if (uascValues.length > 0 && uascValues.length < 2) {
      filters.uasc = uascValues.join(",");
    }

    // Care Status
    const careValues = Object.entries(careChecks)
      .filter(([, checked]) => checked)
      .map(([key]) => key);
    if (careValues.length > 0 && careValues.length < 6) {
      filters.careStatus = careValues.join(",");
    }

    // Subschool (SchoolId)
    if (SchoolsId) {
      filters.subschoolId = SchoolsId;
    }

    // Leavers
    filters.leavers = showLeavers ? "true" : "false";

    // Custom Search - use debounced value
    if (debouncedSearchTerm.trim()) {
      filters.customSearch = debouncedSearchTerm.trim();
    }

    // Report Dates
    if (datePreset === "custom" && dateRange?.start && dateRange?.end) {
      filters.startDate = dateRange.start;
      filters.endDate = dateRange.end;
    } else if (datePreset && datePreset !== "none") {
      filters.reportDates = datePreset;
    }
    
    return filters;
  }, [
    SchoolsId,
    yearGroupSelection,
    attendanceSelection,
    genderSelection,
    laSelection,
    senChecks,
    ealSelection,
    uascSelection,
    careChecks,
    debouncedSearchTerm, // Use debounced search term
    showLeavers,
    datePreset,
    dateRange,
  ]);

  // ✅ Send filters to parent with debouncing and change detection
  const [lastFiltersHash, setLastFiltersHash] = useState('');
  
  useEffect(() => {
    const timer = setTimeout(() => {
      const filters = buildFilters();
      const filtersHash = JSON.stringify(filters);
      
      // Only send filters if they actually changed
      if (filtersHash !== lastFiltersHash) {
        onFiltersChange?.(filters);
        setLastFiltersHash(filtersHash);
      }
    }, 300); // 300ms debounce for filter changes
    
    return () => clearTimeout(timer);
  }, [buildFilters, onFiltersChange, lastFiltersHash]);


  useEffect(() => {
    const fetchSubSchools = async () => {
      try {
        if (!userId) {
          console.warn("No userId found in localStorage");
          return;
        }

        const response = await axios.get(
          `${settings.API_HOST}/rest/fetchSubSchoolList?schoolId=${423562}&userId=${userId}`,
          {
            timeout: 10000, // 10 second timeout
            headers: {
              'Content-Type': 'application/json',
            }
          }
        );

        const schoolList = response?.data?.subSchools || [];
        setSchools(schoolList);

        // Default to no selection so all schools show until user selects
        setSchoolsId('');
      } catch (error) {
        console.error("Error fetching sub schools:", error);
        // Set empty array on error to prevent UI issues
        setSchools([]);
      }
    };

    fetchSubSchools();
  }, [userId]);

  // ✅ Reset all filters
  const handleReset = () => {
    // Reset to no school selected (show all)
    setSchoolsId('');
    setYearGroupSelection([]);
    setAttendanceSelection([]);
    setGenderSelection([]);
    setLaSelection({ in: true, out: true });
    setSenChecks({ Unknown: true, None: true, 'SEN Support': true, EHCP: true, EHCNA: true });
    setEalSelection({ yes: true, no: true });
    setUascSelection({ yes: false, no: false });
    setCareChecks({ CIC: false, CIN: false, CP: false, PCIN: false, PCP: false, EHCP: false });
    setSearchTerm('');
    setDebouncedSearchTerm(''); // Reset debounced search too
    setDatePreset('custom');
    setLastFiltersHash(''); // Reset filter hash to force update
    onDateRangeChange?.([dayjs('2025-08-01'), dayjs('2025-09-30')]);
  };

  return (
    <div className="aa-filters">
      {/* First row of filters */}
      <div className="aa-row">
        <div className="aa-field">
          <label className="aa-label">Year Group</label>
          <Select>
            <SelectTrigger className="aa-select-trigger" style={{ minWidth: '160px' }}>
              <SelectValue placeholder="Select options" />
              <span className="aa-select-value" style={{ marginLeft: '8px' }}>
                {yearGroupSelection.length === yearGroups.length
                  ? 'All'
                  : yearGroupSelection.length === 0
                    ? 'None'
                    : yearGroupSelection.join(', ')}
              </span>
            </SelectTrigger>
            <SelectContent>
              <div className="aa-select-item" style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <button
                  type="button"
                  className="aa-btn aa-btn-outline aa-btn-sm"
                  onClick={(e) => {
                    e.preventDefault();
                    setYearGroupSelection(yearGroups);
                  }}
                >
                  Check all
                </button>
                <button
                  type="button"
                  className="aa-btn aa-btn-outline aa-btn-sm"
                  onClick={(e) => {
                    e.preventDefault();
                    setYearGroupSelection([]);
                  }}
                >
                  Uncheck all
                </button>
              </div>

              {yearGroups.map((year) => (
                <div
                  key={year}
                  className="aa-select-item"
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <input
                    id={`year-${year}`}
                    type="checkbox"
                    checked={yearGroupSelection.includes(year)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setYearGroupSelection((prev) => [...prev, year]);
                      } else {
                        setYearGroupSelection((prev) => prev.filter((y) => y !== year));
                      }
                    }}
                  />
                  <label htmlFor={`year-${year}`}>{year}</label>
                </div>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="aa-field">
          <label className="aa-label">Attendance Filter</label>
          <Select>
            <SelectTrigger className="aa-select-trigger" style={{ minWidth: '200px' }}>
              <SelectValue placeholder="Select options" />
              <span className="aa-select-value" style={{ marginLeft: '8px' }}>
                {attendanceSelection.length === attendanceOptions.length
                  ? 'All'
                  : attendanceSelection.length === 0
                    ? 'None'
                    : attendanceSelection.join(', ')}
              </span>
            </SelectTrigger>
            <SelectContent>
              <div className="aa-select-item" style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <button
                  type="button"
                  className="aa-btn aa-btn-outline aa-btn-sm"
                  onClick={(e) => {
                    e.preventDefault();
                    setAttendanceSelection(attendanceOptions);
                  }}
                >
                  Check all
                </button>
                <button
                  type="button"
                  className="aa-btn aa-btn-outline aa-btn-sm"
                  onClick={(e) => {
                    e.preventDefault();
                    setAttendanceSelection([]);
                  }}
                >
                  Uncheck all
                </button>
              </div>

              {attendanceOptions.map((opt) => (
                <div
                  key={opt}
                  className="aa-select-item"
                  style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                >
                  <input
                    id={`attendance-${opt}`}
                    type="checkbox"
                    checked={attendanceSelection.includes(opt)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setAttendanceSelection((prev) => [...prev, opt]);
                      } else {
                        setAttendanceSelection((prev) => prev.filter((o) => o !== opt));
                      }
                    }}
                  />
                  <label htmlFor={`attendance-${opt}`}>{opt}</label>
                </div>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="aa-field">
          <label className="aa-label">Report dates</label>
          <div className="aa-row" style={{ gap: '8px', alignItems: 'center' }}>
            <Select
              value={datePreset}
              onValueChange={(val) => {
                setDatePreset(val);
                const today = dayjs();
                if (val === 'today') {
                  onDateRangeChange?.([today, today]);
                } else if (val === 'yesterday') {
                  const y = today.subtract(1, 'day');
                  onDateRangeChange?.([y, y]);
                } else if (val === 'last7') {
                  onDateRangeChange?.([today.subtract(6, 'day'), today]);
                } else if (val === 'last30') {
                  onDateRangeChange?.([today.subtract(29, 'day'), today]);
                } else if (val === 'thisMonth') {
                  onDateRangeChange?.([today.startOf('month'), today.endOf('month')]);
                } else if (val === 'lastMonth') {
                  const start = today.subtract(1, 'month').startOf('month');
                  const end = today.subtract(1, 'month').endOf('month');
                  onDateRangeChange?.([start, end]);
                }
              }}
            >
              <SelectTrigger className="aa-select-trigger" style={{ minWidth: '200px' }}>
                <SelectValue placeholder="Select range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="last7">Last 7 Days</SelectItem>
                <SelectItem value="last30">Last 30 Days</SelectItem>
                <SelectItem value="thisMonth">This Month</SelectItem>
                <SelectItem value="lastMonth">Last Month</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>

            {datePreset === 'custom' && (
              <div className="aa-date-wrap">
                <DatePicker.RangePicker
                  value={Array.isArray(dateRange) ? dateRange : undefined}
                  onChange={(vals) => { setDatePreset('custom'); onDateRangeChange?.(vals); }}
                  className="aa-date-range"
                  format="DD/MM/YYYY"
                  allowClear={false}
                  popupClassName="aa"
                />
                <Calendar className="aa-icon-calendar" />
              </div>
            )}
          </div>
        </div>

        <div className="aa-field">
          <label className="aa-label">School</label>
          <Select
            value={SchoolsId}
            onValueChange={(value) => setSchoolsId(value)}
          >
            <SelectTrigger className="aa-select-trigger" style={{ minWidth: "240px" }}>
              <SelectValue placeholder="Select School" />
            </SelectTrigger>
            <SelectContent>
              {schools.map((school, idx) => (
                <SelectItem
                  key={(school?.[0] ?? school?.[2] ?? idx).toString()}
                  value={(school?.[0] ?? school?.schoolId ?? school?.subschoolId ?? school?.[2] ?? '').toString()}
                >
                  {school?.[2] ?? school?.name ?? school?.schoolName ?? school?.[1] ?? (school?.[0] ?? '').toString()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* ✅ Added Search Input */}
        <div className="aa-field">
          <label className="aa-label">Search</label>
          <Input
            type="text"
            className="aa-input"
            style={{ width: '192px' }}
            placeholder="Search UPN, Name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="aa-row" style={{ marginLeft: 'auto', gap: '8px' }}>
          <Button className="aa-btn aa-btn-primary" onClick={() => {
            try {
              const evt = new CustomEvent('aa-export-attendance');
              window.dispatchEvent(evt);
            } catch (_) { }
          }}>
            EXPORT
          </Button>
          <Button className="aa-btn aa-btn-primary" onClick={handleReset}>
            RESET
          </Button>
        </div>

        <div className="aa-row">
          <span className="aa-label">Show Leavers</span>
          <div>
            <button
              type="button"
              aria-pressed={showLeavers}
              onClick={() => onToggleLeavers?.(!showLeavers)}
              className={`aa-toggle`}
            >
              <span className="aa-toggle-knob" />
            </button>
          </div>
        </div>
      </div>

      {/* Second row of filters */}
      <div className="aa-row">
        <div className="aa-field">
          <label className="aa-label">Gender</label>
          <Select>
            <SelectTrigger className="aa-select-trigger" style={{ minWidth: '176px' }}>
              <SelectValue placeholder="Select options" />
              <span className="aa-select-value" style={{ marginLeft: '8px' }}>
                {genderSelection.length === genderOptions.length
                  ? "All"
                  : genderSelection.length === 0
                    ? "None"
                    : genderSelection.join(", ")}
              </span>
            </SelectTrigger>
            <SelectContent>
              <div className="aa-select-item" style={{ display: "flex", justifyContent: "space-between", gap: "8px" }}>
                <button
                  type="button"
                  className="aa-btn aa-btn-outline aa-btn-sm"
                  onClick={(e) => {
                    e.preventDefault();
                    setGenderSelection(genderOptions);
                  }}
                >
                  Check all
                </button>
                <button
                  type="button"
                  className="aa-btn aa-btn-outline aa-btn-sm"
                  onClick={(e) => {
                    e.preventDefault();
                    setGenderSelection([]);
                  }}
                >
                  Uncheck all
                </button>
              </div>

              {genderOptions.map((opt) => (
                <div
                  key={opt}
                  className="aa-select-item"
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <input
                    id={`gender-${opt}`}
                    type="checkbox"
                    checked={genderSelection.includes(opt)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setGenderSelection((prev) => [...prev, opt]);
                      } else {
                        setGenderSelection((prev) => prev.filter((o) => o !== opt));
                      }
                    }}
                  />
                  <label htmlFor={`gender-${opt}`}>{opt}</label>
                </div>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="aa-field">
          <label className="aa-label">Local Authority</label>
          <Select>
            <SelectTrigger className="aa-select-trigger" style={{ minWidth: '208px' }}>
              <SelectValue placeholder="Select options" />
              <span className="aa-select-value" style={{ marginLeft: '8px' }}>
                {(laSelection.in && laSelection.out)
                  ? 'All'
                  : (!laSelection.in && !laSelection.out)
                    ? 'None'
                    : [laSelection.in ? 'In' : null, laSelection.out ? 'Out' : null].filter(Boolean).join(', ')
                }
              </span>
            </SelectTrigger>
            <SelectContent>
              <div className="aa-select-item" style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <button type="button" className="aa-btn aa-btn-outline aa-btn-sm" onClick={(e) => { e.preventDefault(); setLaSelection({ in: true, out: true }); }}>Check all</button>
                <button type="button" className="aa-btn aa-btn-outline aa-btn-sm" onClick={(e) => { e.preventDefault(); setLaSelection({ in: false, out: false }); }}>Uncheck all</button>
              </div>
              <div className="aa-select-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  id="la-in"
                  type="checkbox"
                  checked={!!laSelection.in}
                  onChange={(e) => setLaSelection(prev => ({ ...prev, in: e.target.checked }))}
                />
                <label htmlFor="la-in">In</label>
              </div>
              <div className="aa-select-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  id="la-out"
                  type="checkbox"
                  checked={!!laSelection.out}
                  onChange={(e) => setLaSelection(prev => ({ ...prev, out: e.target.checked }))}
                />
                <label htmlFor="la-out">Out</label>
              </div>
            </SelectContent>
          </Select>
        </div>

        <div className="aa-field">
          <label className="aa-label">SEN</label>
          <Select>
            <SelectTrigger className="aa-select-trigger" style={{ minWidth: '160px' }}>
              <SelectValue placeholder="Select options" />
              <span className="aa-select-value" style={{ marginLeft: '8px' }}>
                {Object.values(senChecks).every(Boolean)
                  ? 'All'
                  : Object.values(senChecks).every((v) => !v)
                    ? 'None'
                    : Object.entries(senChecks).filter(([, v]) => v).map(([k]) => k).join(', ')
                }
              </span>
            </SelectTrigger>
            <SelectContent>
              <div className="aa-select-item" style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <button
                  type="button"
                  className="aa-btn aa-btn-outline aa-btn-sm"
                  onClick={(e) => { e.preventDefault(); setSenChecks({ Unknown: true, None: true, 'SEN Support': true, EHCP: true, EHCNA: true }); }}
                >
                  Check all
                </button>
                <button
                  type="button"
                  className="aa-btn aa-btn-outline aa-btn-sm"
                  onClick={(e) => { e.preventDefault(); setSenChecks({ Unknown: false, None: false, 'SEN Support': false, EHCP: false, EHCNA: false }); }}
                >
                  Uncheck all
                </button>
              </div>
              {['Unknown', 'None', 'SEN Support', 'EHCP', 'EHCNA'].map((label) => (
                <div key={label} className="aa-select-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    id={`sen-${label}`}
                    type="checkbox"
                    checked={!!senChecks[label]}
                    onChange={(e) => setSenChecks((prev) => ({ ...prev, [label]: e.target.checked }))}
                  />
                  <label htmlFor={`sen-${label}`}>{label}</label>
                </div>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="aa-field">
          <label className="aa-label">EAL</label>
          <Select>
            <SelectTrigger className="aa-select-trigger" style={{ minWidth: '160px' }}>
              <SelectValue placeholder="Select options" />
              <span className="aa-select-value" style={{ marginLeft: '8px' }}>
                {(ealSelection.yes && ealSelection.no)
                  ? 'All'
                  : (!ealSelection.yes && !ealSelection.no)
                    ? 'None'
                    : [ealSelection.yes ? 'YES' : null, ealSelection.no ? 'NO' : null].filter(Boolean).join(', ')
                }
              </span>
            </SelectTrigger>
            <SelectContent>
              <div className="aa-select-item" style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <button type="button" className="aa-btn aa-btn-outline aa-btn-sm" onClick={(e) => { e.preventDefault(); setEalSelection({ yes: true, no: true }); }}>Check all</button>
                <button type="button" className="aa-btn aa-btn-outline aa-btn-sm" onClick={(e) => { e.preventDefault(); setEalSelection({ yes: false, no: false }); }}>Uncheck all</button>
              </div>
              <div className="aa-select-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  id="eal-yes"
                  type="checkbox"
                  checked={!!ealSelection.yes}
                  onChange={(e) => setEalSelection(prev => ({ ...prev, yes: e.target.checked }))}
                />
                <label htmlFor="eal-yes">YES</label>
              </div>
              <div className="aa-select-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  id="eal-no"
                  type="checkbox"
                  checked={!!ealSelection.no}
                  onChange={(e) => setEalSelection(prev => ({ ...prev, no: e.target.checked }))}
                />
                <label htmlFor="eal-no">NO</label>
              </div>
            </SelectContent>
          </Select>
        </div>

        {/* UASC */}
        <div className="aa-field">
          <label className="aa-label">UASC</label>
          <Select>
            <SelectTrigger className="aa-select-trigger" style={{ minWidth: '160px' }}>
              <SelectValue placeholder="Select options" />
              <span className="aa-select-value" style={{ marginLeft: '8px' }}>
                {(uascSelection.yes && uascSelection.no)
                  ? 'All'
                  : (!uascSelection.yes && !uascSelection.no)
                    ? 'None'
                    : [uascSelection.yes ? 'YES' : null, uascSelection.no ? 'NO' : null].filter(Boolean).join(', ')
                }
              </span>
            </SelectTrigger>
            <SelectContent>
              <div className="aa-select-item" style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <button
                  type="button"
                  className="aa-btn aa-btn-outline aa-btn-sm"
                  onClick={(e) => { e.preventDefault(); setUascSelection({ yes: true, no: true }); }}
                >
                  Check all
                </button>
                <button
                  type="button"
                  className="aa-btn aa-btn-outline aa-btn-sm"
                  onClick={(e) => { e.preventDefault(); setUascSelection({ yes: false, no: false }); }}
                >
                  Uncheck all
                </button>
              </div>
              <div className="aa-select-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  id="uasc-yes"
                  type="checkbox"
                  checked={!!uascSelection.yes}
                  onChange={(e) => setUascSelection(prev => ({ ...prev, yes: e.target.checked }))}
                />
                <label htmlFor="uasc-yes">YES</label>
              </div>
              <div className="aa-select-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  id="uasc-no"
                  type="checkbox"
                  checked={!!uascSelection.no}
                  onChange={(e) => setUascSelection(prev => ({ ...prev, no: e.target.checked }))}
                />
                <label htmlFor="uasc-no">NO</label>
              </div>
            </SelectContent>
          </Select>
        </div>

        {/* Care Status */}
        <div className="aa-field">
          <label className="aa-label">Care Status</label>
          <Select>
            <SelectTrigger className="aa-select-trigger" style={{ minWidth: '192px' }}>
              <SelectValue placeholder="Select options" />
              <span className="aa-select-value" style={{ marginLeft: '8px' }}>
                {Object.values(careChecks).every(Boolean)
                  ? 'All'
                  : Object.values(careChecks).every((v) => !v)
                    ? 'None'
                    : Object.entries(careChecks).filter(([, v]) => v).map(([k]) => k).join(', ')
                }
              </span>
            </SelectTrigger>
            <SelectContent>
              <div className="aa-select-item" style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <button
                  type="button"
                  className="aa-btn aa-btn-outline aa-btn-sm"
                  onClick={(e) => {
                    e.preventDefault();
                    setCareChecks({ CIC: true, CIN: true, CP: true, PCIN: true, PCP: true, EHCP: true });
                  }}
                >
                  Check all
                </button>
                <button
                  type="button"
                  className="aa-btn aa-btn-outline aa-btn-sm"
                  onClick={(e) => {
                    e.preventDefault();
                    setCareChecks({ CIC: false, CIN: false, CP: false, PCIN: false, PCP: false, EHCP: false });
                  }}
                >
                  Uncheck all
                </button>
              </div>
              {['CIC', 'CIN', 'CP', 'PCIN', 'PCP', 'EHCP'].map((label) => (
                <div key={label} className="aa-select-item" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    id={`care-${label}`}
                    type="checkbox" checked={!!careChecks[label]}
                    onChange={(e) => setCareChecks((prev) => ({ ...prev, [label]: e.target.checked }))}
                  />
                  <label htmlFor={`care-${label}`}>{label}</label>
                </div>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="aa-row" style={{ marginLeft: 'auto', gap: '8px' }}>
          <Button 
            className="aa-btn aa-btn-primary"
            onClick={() => {
              onMonthNavigation?.('Previous');
              try { const evt = new CustomEvent('aa-month-nav', { detail: 'Previous' }); window.dispatchEvent(evt); } catch (_) {}
            }}
          >
            PREVIOUS MONTH
          </Button>
          <Button 
            className="aa-btn aa-btn-primary"
            onClick={() => {
              onMonthNavigation?.('Next');
              try { const evt = new CustomEvent('aa-month-nav', { detail: 'Next' }); window.dispatchEvent(evt); } catch (_) {}
            }}
          >
            NEXT MONTH
          </Button>
        </div>
      </div>
    </div>
  );
};

// PropTypes for type checking
AttendanceFilters.propTypes = {
  showLea: PropTypes.bool,
  onToggleLeavers: PropTypes.func,
  dateRange: PropTypes.array,
  onDateRangeChange: PropTypes.func,
  onFiltersChange: PropTypes.func,
  onMonthNavigation: PropTypes.func,
};

export default AttendanceFilters;
